import {Tooltip} from '@mantine/core';
import React from 'react';
import _ from 'underscore';

import {canUseBiologicsFeature} from 'antibodies/util/biologicsCapability';
import {BiologicsFeature, RegistryFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import {TagSchemaItemType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getItemTypeDisplayName} from 'schemas/TagSchemaItemTypeUtil';
import FormGroup from 'ui-library/bs/FormGroup';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';

export interface ItemOptionConfiguration {
  defaultOptionName?: string; // If set, used as the name for default option with null value. Otherwise no default option is shown.
  includeMixed?: boolean;
  includeMixture?: boolean;
}

export interface TagSchemaItemTypeSelectorProps {
  disabled?: boolean;
  /**
   * Reason why the dropdown is disabled. When provided and the dropdown is disabled,
   * this text is shown as a tooltip on hover to explain why changing the entity type is not allowed.
   */
  disabledReason?: string;
  displayName: string;
  entityOptionConfiguration?: ItemOptionConfiguration;
  onChangeItemType: (itemType: TagSchemaItemType | null) => void;
  selectedItemType: TagSchemaItemType | null;
  showLabel?: boolean;
  width?: MenuWidth;
}

function getEntityTypeOptions(
  options?: ItemOptionConfiguration
): Array<{value: TagSchemaItemType | null; name: string}> {
  const availableEntityTypes = _.compact([
    TagSchemaItemType.BASIC_FOLDER_ITEM,
    TagSchemaItemType.DNA_SEQUENCE,
    TagSchemaItemType.DNA_OLIGO,
    TagSchemaItemType.RNA_OLIGO,
    TagSchemaItemType.RNA_SEQUENCE,
    options?.includeMixed && TagSchemaItemType.MIXED,
    TagSchemaItemType.AA_SEQUENCE,
    BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE && TagSchemaItemType.MOLECULE,
    BenchlingConfig.ENABLE_ENTRY_SCHEMAS && TagSchemaItemType.ENTRY,
    (isFeatureEnabled({feature: RegistryFeature.REGISTRY_MIXTURES}) || options?.includeMixture) &&
      TagSchemaItemType.MIXTURE,
    canUseBiologicsFeature(BiologicsFeature.BIOLOGICS_EDIT) && TagSchemaItemType.ANTIBODY,
    BenchlingConfig.MODBIO_ENABLE_COMPOSITE_ENTITIES && TagSchemaItemType.OLIGO_CONJUGATE,
    BenchlingConfig.MODBIO_ENABLE_COMPOSITE_ENTITIES && TagSchemaItemType.OLIGO_DUPLEX,
    BenchlingConfig.ENABLE_EQUIPMENT && TagSchemaItemType.EQUIPMENT,
  ]);

  const entityTypeOptions = availableEntityTypes.map((entityType) => ({
    value: entityType,
    name: getItemTypeDisplayName(entityType),
  }));

  return options?.defaultOptionName
    ? [{value: null, name: options?.defaultOptionName}, ...entityTypeOptions]
    : entityTypeOptions;
}

export default function TagSchemaItemTypeSelector(props: TagSchemaItemTypeSelectorProps): JSX.Element {
  const entityOptions = getEntityTypeOptions(props.entityOptionConfiguration);
  const showTooltip = props.disabled && props.disabledReason != null;

  function renderSelectDropdown(): JSX.Element {
    return (
      <Tooltip
        events={{focus: true, hover: true, touch: false}}
        label={props.disabledReason ?? ''}
        disabled={!showTooltip}
        multiline={true}
        width={240}
        withinPortal={true}
      >
        <div>
          <SelectDropdown
            dataTestAttrs={{
              component: 'TagSchemaItemTypeSelector',
              element: 'root',
            }}
            displayName={props.displayName}
            menu={
              <SearchableMenu
                onSelect={props.onChangeItemType}
                selectedValue={props.selectedItemType}
                width={props.width ?? Menu.WIDTHS.FULL}
                optionDatas={entityOptions}
              />
            }
            disabled={props.disabled}
          />
        </div>
      </Tooltip>
    );
  }

  return props.showLabel ? (
    <FormGroup>
      <FieldLabel label="Entity type" />
      {renderSelectDropdown()}
    </FormGroup>
  ) : (
    renderSelectDropdown()
  );
}
